import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { Decision } from "../../api/access/decisions";
import { requirementOf } from "../feedback/requirement";

interface ToggleSwitchProps {
  checked: boolean;
  /** The switch's name for screen readers, unless `labelledBy` names the element that says it. */
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  /** The served decision changing it needs (SOT-02, GS-8): refused about where things are, it isn't shown; otherwise it is disabled, with why. */
  requires?: Decision;
}

/**
 * An on/off switch for something that applies at once, such as a group's permission
 * (WAI-ARIA APG "Switch"): a button with `role="switch"`; Space and Enter flip it.
 */
export default function ToggleSwitch({ requires, ...props }: ToggleSwitchProps) {
  return requires === undefined || requires.refusal === null ? <Switch {...props} /> : <RefusedSwitch decision={requires} {...props} />;
}

function RefusedSwitch({ decision, describedBy, ...props }: Omit<ToggleSwitchProps, "requires"> & { decision: Decision }) {
  const { t } = useTranslation();
  const reasonId = useId();
  const requirement = requirementOf(t, decision);
  if (!requirement.shown) return null;
  return (
    <span title={requirement.reason ?? undefined}>
      <Switch {...props} disabled={true} describedBy={describedBy === undefined ? reasonId : `${describedBy} ${reasonId}`} />
      <span id={reasonId} className="sr-only">{requirement.reason}</span>
    </span>
  );
}

function Switch({ checked, label, labelledBy, describedBy, disabled = false, onChange }: Omit<ToggleSwitchProps, "requires">) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={labelledBy === undefined ? label : undefined}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled}
      className="app-switch"
      onClick={() => {
        onChange(!checked);
      }}
    />
  );
}
